import { useMemo, useState } from "react";

const states = ["AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];
const empty = { first_name: "", last_name: "", email: "", phone: "", zip: "", state: "", consent: false, company: "" };

export default function CampaignCaptureForm({ brand, eyebrow, heading, intro, tracker }) {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const campaign = params.get("lp_campaign") || "";
  const [contact, setContact] = useState(empty);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [continuationUrl, setContinuationUrl] = useState("");
  const set = (key, value) => setContact(current => ({ ...current, [key]: value }));

  async function submit(event) {
    event.preventDefault();
    if (!campaign || !contact.first_name.trim() || !contact.last_name.trim()
      || !/^\S+@\S+\.\S+$/.test(contact.email) || contact.phone.replace(/\D/g, "").length < 10
      || !/^\d{5}$/.test(contact.zip) || !states.includes(contact.state) || !contact.consent) {
      setError("Please complete every required field and consent to continue.");
      return;
    }
    setStatus("submitting");
    setError("");
    try {
      const boot = window.__legacyAttribution || window.__figAttribution || window.__mihAttribution;
      await boot?.syncMetaCookies?.();
      const response = await fetch("/api/campaign_capture.php", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          campaign,
          company: contact.company,
          contact: { ...contact, company: undefined },
          attribution: {
            landing_url: window.location.origin + window.location.pathname,
            referrer: document.referrer,
            query: Object.fromEntries(params.entries()),
            snapshot: boot?.getSnapshot?.(),
          },
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) throw new Error(data.error || "We could not save your request.");
      if (data.event_id && typeof window.fbq === "function") window.fbq("track", "Lead", {}, { eventID: data.event_id });
      tracker?.("campaign_initial_capture", { path: "landing_page" });
      setContinuationUrl(data.continuation_url || "");
      setStatus("success");
    } catch (failure) {
      setError(failure.message || "Please try again.");
      setStatus("error");
    }
  }

  if (status === "success") return <main className="campaign-capture-page"><section className="campaign-capture-card campaign-capture-success">
    <div className="success-check">✓</div><span className="eyebrow">{eyebrow}</span>
    <h1>Your request was received.</h1>
    <p>Your contact information has already been delivered to the licensed professional assigned to this campaign. The optional discovery questions can add context, but they are not required.</p>
    <div className="campaign-capture-actions">{continuationUrl && <a className="campaign-capture-primary" href={continuationUrl}>Continue with optional discovery</a>}<a className="campaign-capture-secondary" href="/">Finish for now</a></div>
  </section></main>;

  return <main className="campaign-capture-page"><div className="campaign-capture-shell"><aside><span className="eyebrow">{eyebrow}</span><h1>{heading}</h1><p>{intro}</p><div className="campaign-capture-assurance">Your lead is delivered as soon as this short form is submitted.<br/>Optional discovery comes afterward.</div></aside><form className="campaign-capture-card" onSubmit={submit}>
    <div className="campaign-capture-brand">{brand}</div><h2>Start with your contact information</h2><p>This first step takes about a minute.</p>
    <div className="campaign-capture-grid">
      <label>First name<input autoComplete="given-name" maxLength="120" value={contact.first_name} onChange={e=>set("first_name",e.target.value)} required /></label>
      <label>Last name<input autoComplete="family-name" maxLength="120" value={contact.last_name} onChange={e=>set("last_name",e.target.value)} required /></label>
      <label>Email<input type="email" autoComplete="email" maxLength="255" value={contact.email} onChange={e=>set("email",e.target.value)} required /></label>
      <label>Mobile phone<input type="tel" autoComplete="tel" maxLength="60" value={contact.phone} onChange={e=>set("phone",e.target.value)} required /></label>
      <label>ZIP code<input inputMode="numeric" autoComplete="postal-code" maxLength="5" value={contact.zip} onChange={e=>set("zip",e.target.value.replace(/\D/g,"").slice(0,5))} required /></label>
      <label>State<select autoComplete="address-level1" value={contact.state} onChange={e=>set("state",e.target.value)} required><option value="">Select state</option>{states.map(state=><option key={state}>{state}</option>)}</select></label>
    </div>
    <label className="campaign-capture-consent"><input type="checkbox" checked={contact.consent} onChange={e=>set("consent",e.target.checked)} required/><span>By checking this box and submitting, I consent to calls and texts about insurance or financial products at the number provided, including by automated technology. Consent is not a condition of purchase. Message and data rates may apply.</span></label>
    <label className="campaign-capture-honeypot" aria-hidden="true">Company<input tabIndex="-1" autoComplete="off" value={contact.company} onChange={e=>set("company",e.target.value)} /></label>
    {error && <div className="form-error" role="alert">{error}</div>}
    <button className="campaign-capture-primary" disabled={status==="submitting"}>{status==="submitting"?"Saving securely…":"Submit my request"}</button>
    <p className="campaign-capture-privacy">Private and secure. This request is routed only to the buyer assigned to this campaign.</p>
  </form></div></main>;
}

